import { AODA } from "@core/utilities/aoda";
import {
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { BaseComponent } from "@shared/components/base.component";
import { FormGroup } from "@angular/forms";
import { startWith, takeWhile, tap, switchMap, exhaustMap, filter } from "rxjs/operators";
import { ElasticsearchQueryService } from "@shared/services/elasticsearch-query.service";
import { Utils } from "@core/utilities/utils";
import { Observable, of, Subject } from "rxjs";

@Component({
  selector: "app-series-filter",
  templateUrl: "./series-filter.component.html",
  styleUrls: ["./series-filter.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SeriesFilter extends BaseComponent implements OnInit, OnDestroy {
  @Input() f: FormGroup;
  @Input() clearFilter: boolean;
  @Input() enableFilter: boolean;
  displaySeriesFilter: boolean = false;
  public height: string;
  totalFilterValues: number = 0;

  //Auto-Complete Variables
  lastViewedPage: number;
  lastFilter = "";
  seriesList: any[] = [];
  selectedSeries: any[] = new Array<any>();
  filteredSeries: any[];
  isAllSelected = false;
  isInitialized = false;
  filteredSeries$: Observable<any[]> | undefined;
  private nextPage$ = new Subject();

  constructor(
    private injector: Injector,
    private el: ElementRef,
    private r2: Renderer2,
    private searchFiltersService: ElasticsearchQueryService
  ) {
    super(injector);
  }

  ngOnInit() {
    super.ngOnInit();

    if(this.f.get("series").value.length > 0) {
      this.displaySeriesFilter = true;
      this.makeSeriesRequest();
    } else {
      this.displaySeriesFilter = false;
    }

    this.selectedSeries = this.f.get("series").value;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    AODA.applyAllFixes(this.el, this.r2, this.languageService.languageType);
  }

  ngOnChanges() {
    //Reset the last viewed page if click outside Series filter area
    this.r2.listen('window', 'click',(e:Event)=>{
      this.lastViewedPage = 1;
      this.tempSeries.setValue("");
      this.getObservableValues();
    });

    if (this.clearFilter) {
      this.unselectAll();
      this.clearFilter = false;
    }

    if (!this.enableFilter) {
      this.displaySeriesFilter = false;
    }
  }

  getSeriesList(startsWith: any, page: number): Observable<any[]> {
    const take = 10;
    const skip = page > 0 ? (page - 1) * take : 0;
    const filtered = this.seriesList.filter((option) =>
      option._source.name_en.toLowerCase().startsWith(startsWith.toLowerCase())
    );
    this.lastViewedPage = page;
    return of(filtered.slice(0, skip + take));
  }

  displayWith(element: any) {
    return element ? element.name_en : null;
  }

  onScroll() {
    this.nextPage$.next(null);
  }

  //Filter Functions
  onDisplaySeriesFilter() {
    if (this.enableFilter) {
      if (!this.displaySeriesFilter && !this.isInitialized) {
        this.makeSeriesRequest();
      }
      this.displaySeriesFilter = !this.displaySeriesFilter;
    }
  }

  makeSeriesRequest() {
    this.isInitialized = true;
    this.searchFiltersService
      .getSeries()
      .pipe(takeWhile(() => this.alive))
      .subscribe((series) => {
        this.totalFilterValues = series.hits.hits.length;
        this.seriesList = this.filteredSeries =
          Utils.sortElasticSearchFilterList(series.hits.hits, "name_en");
        this.getObservableValues();
      });
  }

  getObservableValues() {
    const filter$ = this.tempSeries.valueChanges.pipe(
      startWith(""),
      filter((q) => typeof q === "string")
    );

    this.filteredSeries$ = filter$.pipe(
      switchMap((filter) => {
        let currentPage = this.lastViewedPage;
        return this.nextPage$.pipe(
          startWith(currentPage),
          exhaustMap((_) => this.getSeriesList(filter, currentPage)),
          tap(() => currentPage++)
        );
      })
    );
  }
  //Filter Functions

  isSeriesChecked(item: any) {
    for (var i = 0; i < this.series.value.length; i++) {
      var series = this.series.value[i];
      if (series._source.name_en == item._source.name_en) {
        item.selected = true;
        return true;
      }
    }
    item.selected = false;
    return false;
  }

  isSelected(item: any): boolean {
    return this.series.value.includes(item._source.name_en);
  }

  updateAllSelected() {
    return (this.isAllSelected =
      this.series.value.length == this.totalFilterValues ? true : false);
  }

  //Auto-Complete
  toggleSelectAll(event: any) {
    //Needed for mat-option and mat-checkbox
    if (event.isUserInput || event.source._currentCheckState) {
      this.isAllSelected = !this.isAllSelected;
      this.selectedSeries = [];

      for (let i = 0; i < this.seriesList.length; i++) {
        this.seriesList[i].selected = this.isAllSelected;

        if (this.isAllSelected) {
          this.selectedSeries.push(this.seriesList[i]);
        }
      }
      this.updateSeries(this.selectedSeries);
    }
  }

  unselectAll() {
    for (let i = 0; i < this.seriesList.length; i++) {
      this.seriesList[i].selected = false;
    }
    this.selectedSeries = [];
    this.updateSeries(this.selectedSeries);
  }

  toggleSelection(item: any, event: any) {
    //Needed for mat-option and mat-checkbox
    if (event.isUserInput || event.source._currentCheckState) {
      item.selected = !item.selected;
    if (item.selected && !this.isSelected(item)) {
      this.selectedSeries.push(item);
    } else {
      const i = this.selectedSeries.findIndex(
        (value) => value._source.name_en === item._source.name_en
      );
      this.selectedSeries.splice(i, 1);
    }
    this.updateSeries(this.selectedSeries);
    }
    
  }

  updateSeries(selectedOptions: any[]) {
    var seriesList = [];
    for (var i = 0; i < selectedOptions.length; i++) {
      seriesList.push(selectedOptions[i]);
    }
    this.series.setValue(seriesList);
  }
  //Auto-Complete

  get series() {
    return this.f.get("series");
  }

  get tempSeries() {
    return this.f.get("tempSeries");
  }
}
